<template>
  <v-card class="px-3 pt-3 pb-6 elevation-0">
    <v-card-subtitle class="font-weight-bold midgray--text text-uppercase">
      Change Log
    </v-card-subtitle>

    <v-card-text>
      <div class="change-log-toolbar mb-4">
        <div class="body-2 midgray--text mb-3 mb-md-0">
          {{ filteredEntries.length }} of {{ historyEntries.length }} entries
        </div>

        <v-autocomplete
          v-if="fieldOptions.length"
          v-model="selectedFields"
          :items="fieldOptions"
          item-text="text"
          item-value="value"
          label="Filter changed fields"
          multiple
          chips
          small-chips
          deletable-chips
          :clearable="false"
          dense
          outlined
          hide-details
          class="change-log-filter"
        >
          <template v-slot:prepend-inner>
            <font-awesome-icon
              :icon="['fas', 'filter']"
              size="sm"
              class="midgray--text mt-1"
            />
          </template>

          <template v-slot:item="{ item, on, attrs }">
            <v-list-item
              v-bind="attrs"
              v-on="on"
            >
              <v-list-item-action class="mr-2">
                <font-awesome-icon
                  :icon="attrs['input-value'] ? ['fas', 'square-check'] : ['far', 'square']"
                  size="sm"
                  :class="attrs['input-value'] ? 'primary--text' : 'midgray--text'"
                />
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title>{{ item.text }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>

          <template v-slot:append-outer>
            <v-btn
              v-if="selectedFields.length"
              icon
              x-small
              class="change-log-filter-clear"
              @click.stop="clearSelectedFields"
            >
              <font-awesome-icon
                :icon="['fas', 'circle-xmark']"
                size="sm"
                class="midgray--text"
              />
            </v-btn>
          </template>
        </v-autocomplete>
      </div>

      <v-skeleton-loader
        v-if="loadingHistory"
        type="list-item-three-line@3"
      />

      <div
        v-else-if="historyError"
        class="body-2 toasted--text"
      >
        {{ historyError }}
      </div>

      <div
        v-else-if="!historyEntries.length"
        class="body-2 midgray--text"
      >
        No detailed change history found for this project.
      </div>

      <div
        v-else-if="!filteredEntries.length"
        class="body-2 midgray--text"
      >
        No change-log entries match the selected fields.
      </div>

      <div
        v-else
        class="change-log-list"
      >
        <button
          v-for="entry in filteredEntries"
          :key="entry.id"
          type="button"
          class="change-log-entry"
          @click="openEntry(entry)"
        >
          <div class="change-log-entry__header">
            <div
              class="body-2 font-weight-medium"
              :class="textClass"
            >
              {{ entry.summary }}
            </div>

            <span class="caption primary--text font-weight-medium change-log-entry__cta">
              View details
              <font-awesome-icon
                :icon="['fas', 'chevron-right']"
                size="xs"
                class="ml-1"
              />
            </span>
          </div>

          <div class="caption midgray--text mt-1">
            {{ entry.actor }} | {{ entry.changedAtDisplay }}
          </div>

          <div class="change-log-chip-row mt-3">
            <v-chip
              v-for="fieldLabel in entry.changedFieldLabels"
              :key="`${entry.id}-${fieldLabel}`"
              small
              outlined
              class="mr-2 mb-2"
            >
              {{ fieldLabel }}
            </v-chip>
          </div>
        </button>
      </div>
    </v-card-text>

    <v-dialog
      v-model="detailDialog"
      max-width="920"
    >
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>Change Details</span>

          <v-btn
            icon
            @click="detailDialog = false"
          >
            <font-awesome-icon
              :icon="['fas', 'xmark']"
              size="lg"
            />
          </v-btn>
        </v-card-title>

        <v-card-text v-if="selectedEntry">
          <div class="change-log-detail-meta mb-4">
            <div class="body-2">
              <span class="font-weight-medium">Changed by:</span>
              <span class="ml-1">{{ selectedEntry.actor }}</span>
            </div>

            <div class="body-2 mt-1">
              <span class="font-weight-medium">Changed on:</span>
              <span class="ml-1">{{ selectedEntry.changedAtDisplay }}</span>
            </div>
          </div>

          <v-simple-table class="change-log-detail-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Before</th>
                <th>After</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="detail in selectedEntry.details"
                :key="`${selectedEntry.id}-${detail.field}`"
              >
                <td class="font-weight-medium align-top">
                  {{ detail.label }}
                </td>
                <td class="change-log-detail-table__value align-top">
                  {{ detail.before }}
                </td>
                <td class="change-log-detail-table__value align-top">
                  {{ detail.after }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from '@/store/api.js'
import dates from '@/utils/dates'

const FIELD_LABEL_OVERRIDES = {
  billingCode: 'Billing Code',
  clientId: 'Client ID',
  docId: 'Document ID',
  endDate: 'End Date',
  isActive: 'Status',
  projectCode: 'Project Code',
  projectDescription: 'Project Description',
  projectHours: 'Estimated Hours',
  projectId: 'Project ID',
  projectName: 'Project Name',
  projectPlatforms: 'Platforms',
  projectType: 'Project Type',
  projectTypes: 'Project Types',
  startDate: 'Start Date',
}

const META_FIELDS = [
  'assoc_email',
  'created_dt',
  'docId',
  'pk_change_management',
  'serial_column',
  'table_name',
  'updated_dt',
]

export default {
  name: 'ProjectChangeLog',

  props: {
    project: {
      type: Object,
      required: true,
    },
    textClass: {
      type: String,
      default: 'nearblack--text',
    },
  },

  data() {
    return {
      detailDialog: false,
      historyEntries: [],
      historyError: '',
      loadingHistory: false,
      selectedEntry: null,
      selectedFields: [],
    }
  },

  computed: {
    fieldOptions() {
      const uniqueFields = new Set(
        this.historyEntries.flatMap((entry) => entry.changedFields),
      )

      return Array.from(uniqueFields)
        .sort((leftField, rightField) => this.formatFieldLabel(leftField).localeCompare(this.formatFieldLabel(rightField)))
        .map((field) => ({
          text: this.formatFieldLabel(field),
          value: field,
        }))
    },

    filteredEntries() {
      if (!this.selectedFields.length) {
        return this.historyEntries
      }

      return this.historyEntries.filter((entry) => this.selectedFields.some((field) => entry.changedFields.includes(field)))
    },

    projectId() {
      return this.project?.projectId || null
    },
  },

  watch: {
    projectId: {
      immediate: true,
      async handler(newProjectId) {
        if (!newProjectId) {
          this.historyEntries = []
          this.selectedFields = []
          this.historyError = ''
          return
        }

        await this.fetchHistory(newProjectId)
      },
    },
  },

  methods: {
    clearSelectedFields() {
      this.selectedFields = []
    },

    areValuesEqual(leftValue, rightValue) {
      return JSON.stringify(leftValue ?? null) === JSON.stringify(rightValue ?? null)
    },

    formatDateTime(value) {
      if (!value) {
        return 'Unknown date'
      }

      return dates.getFormattedLocalDateTime(value)
    },

    formatFieldLabel(field) {
      if (FIELD_LABEL_OVERRIDES[field]) {
        return FIELD_LABEL_OVERRIDES[field]
      }

      return field
        .replace(/_/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (character) => character.toUpperCase())
    },

    formatValue(value) {
      if (value === undefined || value === null || value === '') {
        return 'None'
      }

      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No'
      }

      if (Array.isArray(value)) {
        if (!value.length) {
          return 'None'
        }

        return value.map((item) => this.formatValue(item)).join(', ')
      }

      if (typeof value === 'object') {
        return JSON.stringify(value, null, 2)
      }

      return String(value)
    },

    getChangedFields(diffValues, oldValues, newValues) {
      const explicitDiff = Object.keys(diffValues || {})
        .filter((field) => !META_FIELDS.includes(field))

      if (explicitDiff.length) {
        return explicitDiff.sort((leftField, rightField) => this.formatFieldLabel(leftField).localeCompare(this.formatFieldLabel(rightField)))
      }

      const possibleFields = new Set([
        ...Object.keys(oldValues),
        ...Object.keys(newValues),
      ])

      return Array.from(possibleFields)
        .filter((field) => !META_FIELDS.includes(field))
        .filter((field) => !this.areValuesEqual(oldValues[field], newValues[field]))
        .sort((leftField, rightField) => this.formatFieldLabel(leftField).localeCompare(this.formatFieldLabel(rightField)))
    },

    getEntrySummary(changedFieldLabels) {
      if (!changedFieldLabels.length) {
        return 'Changed project metadata'
      }

      if (changedFieldLabels.length === 1) {
        return `Changed ${changedFieldLabels[0]}`
      }

      if (changedFieldLabels.length === 2) {
        return `Changed ${changedFieldLabels[0]} and ${changedFieldLabels[1]}`
      }

      return `Changed ${changedFieldLabels[0]}, ${changedFieldLabels[1]}, and ${changedFieldLabels.length - 2} more fields`
    },

    getObjectValue(objectValue, key) {
      if (!objectValue || typeof objectValue !== 'object') {
        return undefined
      }

      if (Object.prototype.hasOwnProperty.call(objectValue, key)) {
        return objectValue[key]
      }

      return undefined
    },

    normalizeObject(value) {
      if (!value) {
        return {}
      }

      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value)
          return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
        } catch {
          return {}
        }
      }

      if (typeof value === 'object' && !Array.isArray(value)) {
        return value
      }

      return {}
    },

    normalizeEntry(entry) {
      const oldValues = this.normalizeObject(entry.old_values)
      const newValues = this.normalizeObject(entry.new_values)
      const diffValues = this.normalizeObject(entry.value_diff)
      const changedFields = this.getChangedFields(diffValues, oldValues, newValues)
      const changedFieldLabels = changedFields.map((field) => this.formatFieldLabel(field))

      return {
        actor: entry.assoc_email || 'Unknown',
        changedAtDisplay: this.formatDateTime(entry.updated_dt || entry.created_dt),
        changedFieldLabels,
        changedFields,
        details: changedFields.map((field) => ({
          after: this.formatValue(
            this.getObjectValue(newValues, field) !== undefined
              ? this.getObjectValue(newValues, field)
              : this.getObjectValue(diffValues, field),
          ),
          before: this.formatValue(this.getObjectValue(oldValues, field)),
          field,
          label: this.formatFieldLabel(field),
        })),
        id: entry.pk_change_management || `${entry.created_dt || 'unknown'}-${entry.assoc_email || 'unknown'}`,
        summary: this.getEntrySummary(changedFieldLabels),
      }
    },

    openEntry(entry) {
      this.selectedEntry = entry
      this.detailDialog = true
    },

    async fetchHistory(projectId) {
      this.loadingHistory = true
      this.historyError = ''
      this.selectedEntry = null

      try {
        const response = await api.getProjectChangeHistory(projectId)
        const historyRows = Array.isArray(response?.content) ? response.content : []

        this.historyEntries = historyRows.map((entry) => this.normalizeEntry(entry))
        this.selectedFields = this.selectedFields.filter((field) => this.fieldOptions.some((option) => option.value === field))
      } catch {
        this.historyEntries = []
        this.selectedFields = []
        this.historyError = 'Unable to load project change history right now.'
      } finally {
        this.loadingHistory = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.change-log-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.change-log-filter {
  flex: 1 1 320px;
  max-width: 420px;
}

.change-log-filter-clear {
  margin-top: 2px;
}

.change-log-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.change-log-entry {
  width: 100%;
  border: 1px solid rgba(5, 82, 137, 0.15);
  border-radius: 12px;
  background: rgba(5, 82, 137, 0.03);
  padding: 16px;
  text-align: left;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.change-log-entry:hover,
.change-log-entry:focus {
  background: rgba(5, 82, 137, 0.08);
  border-color: rgba(5, 82, 137, 0.3);
  outline: none;
}

.change-log-entry__header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.change-log-entry__cta {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.change-log-chip-row {
  display: flex;
  flex-wrap: wrap;
}

.change-log-detail-table__value {
  white-space: pre-wrap;
  word-break: break-word;
}

.change-log-detail-meta {
  border-left: 3px solid rgba(5, 82, 137, 0.2);
  padding-left: 10px;
}

@media (max-width: 959px) {
  .change-log-filter {
    flex-basis: 100%;
    max-width: none;
  }
}
</style>
